// Courses — one continuous 3D world, one island per course.
// Explicit previous/next controls drive the camera flight, keeping the scene
// composed consistently instead of tying it to browser scroll behaviour.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import CourseWorld from '../components/courses/CourseWorld';
import { COURSES } from '../components/courses/courseData';
import LANDING_CONTENT from '../components/courses/landingContent';
import { injectFonts, T } from '../utils/fonts';
import useReducedMotion from '../utils/useReducedMotion';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

/* ── Course panel ── */
const CoursePanel = ({ course, isActive, index }) => (
  <article
    className={`cw-panel${isActive ? ' is-active' : ''}`}
    style={{ '--accent': course.accent, '--accent-ink': course.accentInk }}
    aria-labelledby={`course-title-${course.id}`}
  >
    <div className="cw-panel-top">
      <span className="gg-node">{course.hud}</span>
      <span className="gg-hud">
        {String(index + 1).padStart(2, '0')} / {String(COURSES.length).padStart(2, '0')}
      </span>
    </div>

    <h2 className="cw-title" id={`course-title-${course.id}`}>
      {course.title}
    </h2>
    <p className="cw-desc">{course.description}</p>

    {course.scene.kind === 'placeholder' && (
      <p className="cw-placeholder-note">🚧 סצנת התצוגה לקורס זה זמנית — המודל הסופי בהכנה.</p>
    )}

    <div className="cw-specs">
      <Spec icon="💰" label="מחיר לחודש" value={`₪${course.price}`} />
      <Spec icon="👦" label="גילאים" value={course.ageRange} />
      <Spec icon="📅" label="שעות שבועיות" value={course.schedule} wide />
      <Spec icon="⏱️" label="היקף הקורס" value={course.totalHours} wide />
    </div>

    <div className="cw-actions">
      <Link className="gg-btn" to="/contact">
        צור קשר להרשמה
      </Link>
      {/* A course with its own landing page sends people there — it holds the
          full topic list and links the PDF both, so the site and the campaigns
          share one destination. Otherwise offer the PDF directly, and only when
          one actually exists: a download button pointing at a missing file is
          worse than no button. */}
      {LANDING_CONTENT[course.id] ? (
        <Link className="gg-btn-ghost" to={LANDING_CONTENT[course.id].path}>
          📄 לפרטים ולסילבוס
        </Link>
      ) : course.syllabusLink && (
        <a className="gg-btn-ghost" href={course.syllabusLink} download>
          📄 הורד סילבוס
        </a>
      )}
    </div>
  </article>
);

const Spec = ({ icon, label, value, wide }) => (
  <div className={`cw-spec${wide ? ' is-wide' : ''}`}>
    <span className="cw-spec-icon" aria-hidden="true">{icon}</span>
    <div>
      <p className="cw-spec-label">{label}</p>
      <p className="cw-spec-value">{value}</p>
    </div>
  </div>
);

/* ── Page ── */
const Courses = () => {
  const progressRef = useRef(0);
  const swipeStartRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [worldReady, setWorldReady] = useState(false);
  const reduced = useReducedMotion();
  const isMobile = useIsMobile();
  const handleWorldReady = useCallback(() => setWorldReady(true), []);

  useEffect(() => { injectFonts(); }, []);
  useEffect(() => {
    document.documentElement.classList.add('courses-view');
    const resetScroll = () => window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    resetScroll();
    const frame = requestAnimationFrame(resetScroll);
    // Some mobile browsers restore the previous route's scroll position just
    // after React mounts; this final reset wins that race on direct entry.
    const timer = setTimeout(resetScroll, 80);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      document.documentElement.classList.remove('courses-view');
    };
  }, []);

  const goToFooter = useCallback(() => {
    const footer = document.querySelector('body > #root > footer');
    if (!footer) return;
    const top = footer.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top, left: 0, behavior: reduced ? 'auto' : 'smooth' });
  }, [reduced]);

  const moveBy = useCallback((amount) => {
    if (amount > 0 && progressRef.current >= COURSES.length - 1) {
      goToFooter();
      return;
    }

    setActiveIndex((current) => {
      const next = Math.max(0, Math.min(COURSES.length - 1, current + amount));
      progressRef.current = next;
      return next;
    });
  }, [goToFooter]);

  const onTouchStart = useCallback((event) => {
    if (event.touches.length !== 1) return;
    const target = event.target;
    if (target instanceof HTMLElement && target.closest('a, button, input, textarea, select, [contenteditable="true"]')) return;

    const touch = event.touches[0];
    swipeStartRef.current = { x: touch.clientX, y: touch.clientY, time: performance.now() };
  }, []);

  const onTouchEnd = useCallback((event) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start || event.changedTouches.length === 0) return;

    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const elapsed = performance.now() - start.time;

    // Require a deliberate, mostly vertical gesture. Short taps and diagonal
    // drags still reach the interactive 3D objects and course links normally.
    if (elapsed > 900 || Math.abs(dy) < 48 || Math.abs(dy) < Math.abs(dx) * 1.2) return;

    // If the footer has shifted the scene out of full view, a downward finger
    // gesture always restores the scene first. It must not also change course,
    // otherwise the visitor sees only a cropped part of the new island.
    const footer = document.querySelector('body > #root > footer');
    const footerVisible = footer && footer.getBoundingClientRect().top < window.innerHeight - 1;
    if (dy > 0 && footerVisible) {
      window.scrollTo({ top: 0, left: 0, behavior: reduced ? 'auto' : 'smooth' });
      return;
    }

    moveBy(dy < 0 ? 1 : -1);
  }, [moveBy, reduced]);

  // Some mobile browsers occasionally promote a WebGL tap into a document
  // selection because react-three-fiber delegates its pointer events through
  // the React root. Clear that transient range without touching real controls
  // or preventing the mesh click itself.
  const clearSceneSelection = useCallback((event) => {
    const target = event.target;
    if (target instanceof HTMLElement
      && target.closest('a, button, input, textarea, select, summary, [contenteditable="true"]')) {
      return;
    }
    window.getSelection()?.removeAllRanges();
  }, []);

  // Matching keyboard arrows complement the visible controls and touch swipes
  // without turning the mouse wheel into a hidden second interaction model.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      const target = event.target;
      if (target instanceof HTMLElement && target.closest('input, textarea, select, [contenteditable="true"]')) return;
      event.preventDefault();
      moveBy(event.key === 'ArrowDown' ? 1 : -1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [moveBy]);

  const css = useMemo(() => styles(), []);

  return (
    <div
      className="cw-root"
      dir="rtl"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={() => { swipeStartRef.current = null; }}
      onPointerDownCapture={clearSceneSelection}
      onPointerUpCapture={clearSceneSelection}
    >
      <style>{css}</style>

      <div className="cw-stage" aria-hidden="true">
        <CourseWorld
          progressRef={progressRef}
          reduced={reduced}
          isMobile={isMobile}
          onReady={handleWorldReady}
        />
        <div className={`cw-world-loading${worldReady ? ' is-ready' : ''}`}>
          <span className="cw-world-spinner" />
          <span>טוען את עולם הקורסים...</span>
        </div>
      </div>

      {/* Screen-reader / no-WebGL heading for the page as a whole */}
      <h1 className="cw-sr-title">קורסי למידה — GoGameDev3D</h1>

      <section className="cw-section" aria-live="polite" aria-atomic="true">
        <CoursePanel
          key={COURSES[activeIndex].id}
          course={COURSES[activeIndex]}
          index={activeIndex}
          isActive
        />
      </section>

      {/* Passive position rail; the arrows below are the only navigation. */}
      <div className="cw-rail" aria-hidden="true">
        {COURSES.map((course, i) => (
          <span
            key={course.id}
            className={`cw-rail-dot${i === activeIndex ? ' is-active' : ''}`}
            style={{ '--accent': course.accent, '--accent-ink': course.accentInk }}
          >
            <span className="cw-rail-label">{course.title}</span>
          </span>
        ))}
      </div>

      <nav className="cw-arrows" aria-label="הקורס הקודם והבא">
        <button
          type="button"
          className="cw-arrow"
          onClick={() => moveBy(-1)}
          disabled={activeIndex === 0}
          aria-label="עבור לקורס הקודם"
        >
          <span aria-hidden="true">↑</span>
        </button>
        <span className="cw-arrow-count" aria-hidden="true">
          {String(activeIndex + 1).padStart(2, '0')} / {String(COURSES.length).padStart(2, '0')}
        </span>
        <button
          type="button"
          className="cw-arrow"
          onClick={() => moveBy(1)}
          aria-label={activeIndex === COURSES.length - 1 ? 'עבור לסוף העמוד' : 'עבור לקורס הבא'}
        >
          <span aria-hidden="true">↓</span>
        </button>
      </nav>
    </div>
  );
};

const styles = () => `
  :root.courses-view {
    scroll-behavior: smooth;
    overscroll-behavior-y: contain;
  }

  .cw-root {
    position: relative;
    height: 100vh;
    /* Stable while mobile browser chrome expands/collapses. Using dvh (or
       visualViewport.height) here resized the WebGL drawing buffer mid-scroll. */
    height: 100svh;
    overflow: hidden;
    touch-action: pan-x;
    background: ${T.bg};
    font-family: ${T.fontBody};
    color: ${T.text};
  }

  .cw-stage {
    position: absolute;
    inset: 0;
    height: 100%;
    width: 100%;
    z-index: 0;
    /* No entrance animation here on purpose. The camera supplies it — see the
       entrance damp in CameraRig — and animating the canvas box itself left a
       thin unpainted edge in some Android compositors until the next scroll
       forced a repaint. */
  }

  .cw-world-loading {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    background: radial-gradient(circle at 50% 38%, #111D38 0%, ${T.bg} 68%);
    color: ${T.godotLight};
    font-weight: 700;
    letter-spacing: 0.02em;
    opacity: 1;
    visibility: visible;
    transition: opacity .28s ease, visibility 0s linear .28s;
    pointer-events: none;
  }
  :root[data-theme="light"] .cw-world-loading {
    background: radial-gradient(circle at 50% 32%, #B8E2F6 0%, #E5F5FC 72%);
  }
  .cw-world-loading.is-ready { opacity: 0; visibility: hidden; }
  .cw-world-spinner {
    width: 38px;
    height: 38px;
    border: 3px solid ${T.line};
    border-top-color: ${T.godot};
    border-radius: 50%;
    animation: cwSpin .85s linear infinite;
  }
  @keyframes cwSpin { to { transform: rotate(360deg); } }

  .cw-section {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0 clamp(18px, 6vw, 90px);
    box-sizing: border-box;
    pointer-events: none;
  }

  .cw-sr-title {
    position: absolute;
    width: 1px; height: 1px;
    overflow: hidden; clip: rect(0 0 0 0); clip-path: inset(50%);
    white-space: nowrap;
  }

  /* ── Panel ── */
  .cw-panel {
    position: relative;
    pointer-events: auto;
    box-sizing: border-box;
    width: clamp(300px, 36vw, 470px);
    padding: clamp(18px, 2.6vw, 30px);
    background: ${T.panelGlass};
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    border: 1px solid ${T.line};
    border-radius: 18px;
    box-shadow: 0 28px 70px rgba(0, 0, 0, 0.6);
    opacity: 0;
    transform: translateY(34px) scale(0.985);
    transition: opacity .55s cubic-bezier(.2,.8,.2,1), transform .55s cubic-bezier(.2,.8,.2,1);
  }
  .cw-panel.is-active {
    opacity: 1;
    transform: translateY(0) scale(1);
    animation: cwPanelIn .58s .14s cubic-bezier(.2,.8,.2,1) both;
  }
  @keyframes cwPanelIn {
    from { opacity: 0; transform: translateY(28px) scale(.98); filter: blur(4px); }
    to   { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
  }
  .cw-panel::after { /* accent edge, keeps each course visually tagged */
    content: '';
    position: absolute;
    inset: auto 0 0 0;
    height: 2px;
    border-radius: 0 0 18px 18px;
    background: linear-gradient(90deg, transparent, var(--accent), transparent);
    opacity: .8;
  }

  .cw-panel-top {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; margin-bottom: clamp(12px, 2vw, 18px);
  }
  .cw-panel .gg-node { color: var(--accent); }
  .cw-panel .gg-node::before { background: var(--accent); box-shadow: 0 0 10px var(--accent); }
  .cw-panel .gg-node::after { background: linear-gradient(270deg, var(--accent), transparent); }
  /* The gems glow on night navy but sit at 1.5-2.6:1 on white, so on the light
     theme anything that reads as text or a state dot uses the darker ink. */
  :root[data-theme="light"] .cw-panel .gg-node { color: var(--accent-ink); }
  :root[data-theme="light"] .cw-panel { box-shadow: 0 28px 70px rgba(26, 59, 92, 0.30); }
  :root[data-theme="light"] .cw-panel .gg-node::before { background: var(--accent-ink); box-shadow: none; }
  :root[data-theme="light"] .cw-panel .gg-node::after { background: linear-gradient(270deg, var(--accent-ink), transparent); }

  .cw-title {
    font-family: ${T.fontDisplay};
    font-size: clamp(1.5rem, 3.2vw, 2.3rem);
    line-height: 1.25;
    margin: 0 0 10px;
    color: ${T.text};
  }
  .cw-desc {
    margin: 0 0 clamp(14px, 2vw, 20px);
    color: ${T.muted};
    font-size: clamp(0.95rem, 1.3vw, 1.05rem);
    line-height: 1.7;
  }
  .cw-placeholder-note {
    margin: -6px 0 16px;
    padding: 9px 12px;
    border-radius: 10px;
    background: ${T.goldSoft};
    border: 1px dashed ${T.goldLine};
    color: ${T.goldInkSoft};
    font-size: 0.84rem;
    line-height: 1.5;
  }

  .cw-specs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin: 0 0 clamp(16px, 2.2vw, 22px);
  }
  .cw-spec {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px;
    border: 1px solid ${T.lineSoft};
    border-radius: 11px;
    background: ${T.panelUp};
  }
  .cw-spec.is-wide { grid-column: 1 / -1; }
  .cw-spec-icon { font-size: 1.15rem; flex-shrink: 0; }
  .cw-spec-label {
    font-size: 0.66rem; font-weight: 700; color: ${T.faint};
    letter-spacing: .06em; margin: 0 0 2px;
  }
  .cw-spec-value { margin: 0; font-size: 0.88rem; font-weight: 700; color: ${T.text}; line-height: 1.35; }

  .cw-actions { display: flex; flex-direction: column; gap: 9px; }
  .cw-actions .gg-btn, .cw-actions .gg-btn-ghost { width: 100%; box-sizing: border-box; }

  /* ── Island rail ── */
  .cw-rail {
    position: absolute;
    left: clamp(12px, 2vw, 26px);
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
    display: flex; flex-direction: column; gap: 14px;
    transition: opacity .3s ease;
  }
  .cw-rail-dot {
    position: relative;
    display: block;
    box-sizing: border-box;
    width: 12px; height: 12px;
    border-radius: 50%;
    border: 1.5px solid ${T.line};
    background: ${T.panelGlass};
    transition: background .25s ease, border-color .25s ease, transform .25s ease;
  }
  .cw-rail-dot.is-active {
    background: var(--accent);
    border-color: var(--accent);
    transform: scale(1.35);
    box-shadow: 0 0 14px var(--accent);
  }
  .cw-rail-label {
    position: absolute; right: 22px; top: 50%; transform: translateY(-50%) translateX(6px);
    white-space: nowrap; font-size: 0.76rem; font-weight: 700; color: ${T.muted};
    background: ${T.panel}; border: 1px solid ${T.line};
    padding: 5px 10px; border-radius: 8px;
    opacity: 0; pointer-events: none;
    transition: opacity .2s ease, transform .2s ease;
  }

  /* Previous / next controls are the only way the course changes, so unlike
     the old floating down hint both directions stay visible and unambiguous. */
  .cw-arrows {
    position: absolute;
    z-index: 6;
    left: 50%;
    bottom: clamp(16px, 3vh, 30px);
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 10px;
    direction: ltr;
  }
  .cw-arrow {
    z-index: 5;
    width: 46px;
    height: 46px;
    padding: 0;
    border-radius: 50%;
    border: 1px solid ${T.line};
    background: ${T.panelGlass};
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    color: ${T.text};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: ${T.fontBody};
    font-weight: 800;
    font-size: 1.15rem;
    cursor: pointer;
    transition: border-color .25s ease, background .25s ease, color .25s ease, transform .25s ease, opacity .25s ease;
  }
  .cw-arrow:hover:not(:disabled) { border-color: ${T.focus}; background: ${T.panelUp}; transform: translateY(-2px); }
  .cw-arrow:focus-visible { outline: 2px solid ${T.gold}; outline-offset: 3px; }
  .cw-arrow:disabled { opacity: .32; cursor: default; }
  .cw-arrow-count {
    min-width: 64px;
    text-align: center;
    color: ${T.muted};
    font-family: ${T.fontDisplay};
    font-size: .76rem;
    letter-spacing: .12em;
  }

  /* Mobile: keep the whole card in the page flow. The old percentage cap
     created a second scrollbar on short phones and clipped the actions. */
  @media (max-width: 768px) {
    .cw-section {
      align-items: flex-end;
      justify-content: center;
      padding: 0 12px calc(env(safe-area-inset-bottom, 0px) + 60px);
    }
    .cw-panel {
      width: 100%;
      max-width: 430px;
      padding: 12px;
      border-radius: 16px;
      max-height: none;
      overflow: visible;
      /* Live backdrop-filter recomposites the WebGL scene behind the whole
         card on every animation frame. The mobile panel is effectively
         opaque, so removing it preserves the look and avoids that cost. */
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }
    .cw-panel-top { display: none; }
    .cw-title { font-size: clamp(1.18rem, 5.2vw, 1.45rem); margin-bottom: 4px; }
    .cw-desc { font-size: 0.82rem; line-height: 1.4; margin-bottom: 8px; }
    .cw-placeholder-note { margin: -2px 0 8px; padding: 6px 8px; font-size: 0.72rem; }
    .cw-specs { gap: 6px; margin-bottom: 9px; }
    .cw-spec.is-wide { grid-column: auto; }
    .cw-spec { min-width: 0; padding: 7px 8px; gap: 6px; align-items: flex-start; }
    .cw-spec > div { min-width: 0; }
    .cw-spec-icon { font-size: 0.9rem; }
    .cw-spec-label { font-size: 0.56rem; letter-spacing: .035em; }
    .cw-spec-value { font-size: clamp(0.68rem, 3vw, 0.78rem); line-height: 1.25; overflow-wrap: anywhere; }
    .cw-actions { flex-direction: row; gap: 8px; }
    .cw-actions .gg-btn, .cw-actions .gg-btn-ghost {
      min-height: 42px;
      padding: 9px 8px;
      font-size: clamp(0.72rem, 3.3vw, 0.82rem);
    }
    .cw-rail { left: 8px; gap: 12px; }
    .cw-rail-label { display: none; }
    .cw-arrows { bottom: calc(env(safe-area-inset-bottom, 0px) + 7px); gap: 8px; }
    .cw-arrow { width: 44px; height: 44px; }
    .cw-arrow-count { min-width: 54px; font-size: .68rem; }
  }

  @media (max-width: 768px) and (max-height: 700px) {
    .cw-section { padding-inline: 10px; padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 56px); }
    .cw-panel { padding: 10px; }
    .cw-title { font-size: 1.08rem; }
    .cw-desc { font-size: 0.76rem; margin-bottom: 7px; }
    .cw-specs { gap: 5px; margin-bottom: 7px; }
    .cw-spec { padding: 5px 6px; gap: 5px; }
    .cw-spec-label { font-size: 0.52rem; }
    .cw-spec-value { font-size: 0.66rem; }
    .cw-actions .gg-btn, .cw-actions .gg-btn-ghost { min-height: 40px; padding-block: 7px; }
  }

  @media (prefers-reduced-motion: reduce) {
    .cw-panel { opacity: 1; transform: none; transition: none; }
    .cw-panel.is-active { animation: none; }
  }
  :root.stop-animations .cw-panel { opacity: 1; transform: none; }
`;

export default Courses;
